import { adTypeMap } from "@/http/api-enums";
import { CalibrationTableRow } from "@/http/api-types";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { getGradientColor } from "../../efficiency/color_range";

const lowHex = "#FFF59D"; // yellow
const highHex = "#EF9A9A"; // red

const InstallsPerCostBucket: ColumnDef<CalibrationTableRow> = {
  accessorKey: "InstallsPerCostBucket",
  header: "IPC Bucket",
};

const AdType: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AdType",
  header: "Ad Type",
  cell: adTypeCell,
};

const AvgPredictedInstallsPerCost: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgPredictedInstallsPerCost",
  header: "Predicted IPC",
  cell: countCell,
};

const InstallsPerCost: ColumnDef<CalibrationTableRow> = {
  accessorKey: "InstallsPerCost",
  header: "IPC",
  cell: countCell,
};

const InstallsPerCostCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "InstallsPerCostCalibration",
  header: "IPC calibration",
  cell: percentCell,
};

const AvgImpValueMicroEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgImpValueMicroEur",
  header: "Avg. Imp Value",
  cell: microEuroCell,
};

const AvgMaxBidMicroEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgMaxBidMicroEur",
  header: "Avg. Max Bid",
  cell: microEuroCell,
};

const Conversions: ColumnDef<CalibrationTableRow> = {
  accessorKey: "Conversions",
  header: "Conversions",
  cell: countCell,
};

const ApConversions: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ApConversions",
  header: "AP Conversions",
  cell: countCell,
};

const Bids: ColumnDef<CalibrationTableRow> = {
  accessorKey: "Bids",
  header: "Bids",
  cell: countCell,
};

const Impressions: ColumnDef<CalibrationTableRow> = {
  accessorKey: "Impressions",
  header: "Impressions",
  cell: countCell,
};

const AvgBidPriceCpmMicroEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgBidPriceCpmMicroEur",
  header: "Avg. Bid Price CPM",
  cell: microEuroCell,
};

const AvgWinPriceCpmMicroEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgWinPriceCpmMicroEur",
  header: "Avg. Win Price CPM",
  cell: microEuroCell,
};

const AvgEstimatedWinRate: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgEstimatedWinRate",
  header: "Avg. Estimated Win Rate",
  cell: percentCell,
};

const WinRate: ColumnDef<CalibrationTableRow> = {
  accessorKey: "WinRate",
  header: "Win Rate",
  cell: percentCell,
};

const ImpPerBids: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ImpPerBids",
  header: "Impressions Per Bids",
  cell: percentCell,
};

const AvgTotalBidMultiplier: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgTotalBidMultiplier",
  header: "Avg. Bid Multiplier",
  cell: percentCell,
};

const AvgSpendAdjustment: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgSpendAdjustment",
  header: "Avg. Spend Adjustment",
  cell: percentCell,
};

const AvgCorrectionCoefficient: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgCorrectionCoefficient",
  header: "Avg. Correction Coefficient",
  cell: percentCell,
};

const AvgEstimatedIpm: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgEstimatedIpm",
  header: "Avg. Estimated IPM",
  cell: percentCell,
};

const ApIpm: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ApIpm",
  header: "AP IPM",
  cell: percentCell,
};

const ApCpiEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ApCpiEur",
  header: "AP CPI",
  cell: euroCell,
};

const PredictedBidCpiEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "PredictedBidCpiEur",
  header: "Predicted Bid CPI",
  cell: euroCell,
};

const PredictedImpCpiEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "PredictedImpCpiEur",
  header: "Predicted Imp CPI",
  cell: euroCell,
};

const SupplyCostEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "SupplyCostEur",
  header: "Supply Cost",
  cell: euroCell,
};

const RevenueEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "RevenueEur",
  header: "Revenue",
  cell: euroCell,
};

const ProfitEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ProfitEur",
  header: "Profit",
  cell: euroCell,
};

const AvgDynamicTargetMargin: ColumnDef<CalibrationTableRow> = {
  accessorKey: "AvgDynamicTargetMargin",
  header: "Margin",
  cell: percentCell,
};

const EstimatedInfraCostEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "EstimatedInfraCostEur",
  header: "Estimated Infra Cost",
  cell: euroCell,
};

const GrossProfitEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "GrossProfitEur",
  header: "Gross Profit",
  cell: euroCell,
};

const GrossMargin: ColumnDef<CalibrationTableRow> = {
  accessorKey: "GrossMargin",
  header: "Gross Margin",
  cell: percentCell,
};

const InstallRateCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "InstallRateCalibration",
  header: "IPM Calibration",
  cell: percentCell,
};

const CostPerInstallCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "CostPerInstallCalibration",
  header: "CPI Calibration",
  cell: percentCell,
};

const ApInfraCpiEur: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ApInfraCpiEur",
  header: "AP +Infra CPI",
  cell: euroCell,
};

const WinRateCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "WinRateCalibration",
  header: "Win Rate Calibration",
  cell: percentCell,
};

const ImpressionsPerBidCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "ImpressionsPerBidCalibration",
  header: "Imps/Bids Calibration",
  cell: percentCell,
};

const BidShading: ColumnDef<CalibrationTableRow> = {
  accessorKey: "BidShading",
  header: "Bid Shading",
  cell: percentCell,
};

const WinPriceCalibration: ColumnDef<CalibrationTableRow> = {
  accessorKey: "WinPriceCalibration",
  header: "Win Price Calibration",
  cell: percentCell,
};

const InfraShareOfProfit: ColumnDef<CalibrationTableRow> = {
  accessorKey: "InfraShareOfProfit",
  header: "Infra of Profit",
  cell: percentCell,
};

export const columnsOverview: ColumnDef<CalibrationTableRow>[] = [
  InstallsPerCostBucket,
  AdType,
  AvgPredictedInstallsPerCost,
  InstallsPerCost,
  InstallsPerCostCalibration,
  Bids,
  Impressions,
  ImpPerBids,
  RevenueEur,
  GrossProfitEur,
  GrossMargin,
  AvgBidPriceCpmMicroEur,
  AvgWinPriceCpmMicroEur,
];

export const columnsERP: ColumnDef<CalibrationTableRow>[] = [
  InstallsPerCostBucket,
  AdType,
  AvgPredictedInstallsPerCost,
  InstallsPerCost,
  RevenueEur,
  AvgEstimatedIpm,
  ApIpm,
  InstallRateCalibration,
  PredictedBidCpiEur,
  PredictedImpCpiEur,
  ApCpiEur,
  CostPerInstallCalibration,
  ApInfraCpiEur,
];

export const columnsCPM: ColumnDef<CalibrationTableRow>[] = [
  InstallsPerCostBucket,
  AdType,
  AvgPredictedInstallsPerCost,
  InstallsPerCost,
  RevenueEur,
  AvgEstimatedWinRate,
  WinRate,
  WinRateCalibration,
  ImpPerBids,
  ImpressionsPerBidCalibration,
  AvgBidPriceCpmMicroEur,
  AvgWinPriceCpmMicroEur,
  BidShading,
  WinPriceCalibration,
];

export const columnsPricing: ColumnDef<CalibrationTableRow>[] = [
  InstallsPerCostBucket,
  AdType,
  AvgPredictedInstallsPerCost,
  InstallsPerCost,
  AvgEstimatedIpm,
  AvgTotalBidMultiplier,
  AvgImpValueMicroEur,
  AvgSpendAdjustment,
  AvgCorrectionCoefficient,
  AvgMaxBidMicroEur,
  AvgBidPriceCpmMicroEur,
  AvgWinPriceCpmMicroEur,
];

export const columnsEfficiency: ColumnDef<CalibrationTableRow>[] = [
  InstallsPerCostBucket,
  AdType,
  AvgPredictedInstallsPerCost,
  InstallsPerCost,
  Bids,
  Impressions,
  AvgEstimatedWinRate,
  ImpPerBids,
  ImpressionsPerBidCalibration,
  RevenueEur,
  ProfitEur,
  AvgDynamicTargetMargin,
  EstimatedInfraCostEur,
  InfraShareOfProfit,
  GrossProfitEur,
  GrossMargin,
];

const euroFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  maximumSignificantDigits: 4,

  notation: "compact",
  compactDisplay: "short",
});

function microEuroCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = euroFormatter.format(value / 1e6);

  if (x.row.original["InstallsPerCostBucket"] == "Total") {
    return <div className="p-1 text-right font-medium">{formatted}</div>;
  }

  return (
    <div className="p-1 text-right font-medium" style={{ backgroundColor: col, color: "#000" }}>
      {formatted}
    </div>
  );
}

function euroCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = euroFormatter.format(value);

  if (x.row.original["InstallsPerCostBucket"] == "Total") {
    return <div className="p-1 text-right font-medium">{formatted}</div>;
  }

  return (
    <div className="p-1 text-right font-medium" style={{ backgroundColor: col, color: "#000" }}>
      {formatted}
    </div>
  );
}

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumSignificantDigits: 4,
  notation: "compact",
  compactDisplay: "short",
});

function percentCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = percentFormatter.format(value);

  if (x.row.original["InstallsPerCostBucket"] == "Total") {
    return <div className="p-1 text-right font-medium">{formatted}</div>;
  }

  return (
    <div className="p-1 text-right font-medium" style={{ backgroundColor: col, color: "#000" }}>
      {formatted}
    </div>
  );
}

const countFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumSignificantDigits: 4,
  notation: "compact",
  compactDisplay: "short",
});

function countCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = countFormatter.format(value);

  if (x.row.original["InstallsPerCostBucket"] == "Total") {
    return <div className="p-1 text-right font-medium">{formatted}</div>;
  }

  return (
    <div className="p-1 text-right font-medium" style={{ backgroundColor: col, color: "#000" }}>
      {formatted}
    </div>
  );
}

function adTypeCell({ row }: CellContext<CalibrationTableRow, unknown>): JSX.Element {
  const value = row.getValue("AdType") as string;
  return <div className="p-1 text-right font-medium">{adTypeMap[value] || "Unknown"}</div>;
}
